import { useMemo } from 'react';
import {
  type DiscoveryApi,
  discoveryApiRef,
  type FetchApi,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { TESSA_LICENSE_TYPES_ROUTE_PATH } from '@ns/plugin-ns-tessa-common';
import type { OptionSource } from '@ns/plugin-picker-toolbox-contracts';
import { z } from 'zod';

const TESSA_LICENSE_TYPES_PLUGIN_ID = 'catalog';

export interface TessaLicenseTypeItem {
  id: string;
  name: string;
}

const EXCLUDED_TESSA_LICENSE_TYPE_IDS = new Set(['jiraservicemanagement']);

const tessaLicenseTypeItemSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
  })
  .strict() satisfies z.ZodType<TessaLicenseTypeItem>;

const tessaLicenseTypesResponseSchema = z
  .object({
    items: z.array(tessaLicenseTypeItemSchema),
  })
  .strict();

export function mapTessaLicenseTypesResponse(
  payload: unknown,
): TessaLicenseTypeItem[] {
  const parsed = tessaLicenseTypesResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error('Tessa license types response has an invalid shape.');
  }
  return parsed.data.items.filter(
    item => !EXCLUDED_TESSA_LICENSE_TYPE_IDS.has(item.id),
  );
}

export function createTessaLicenseTypeSource(deps: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}): OptionSource<TessaLicenseTypeItem, TessaLicenseTypeItem> {
  const { discoveryApi, fetchApi } = deps;
  return {
    getOptionLabel(item) {
      return item.name;
    },
    getOptionValue(item) {
      return item;
    },
    async resolve(input) {
      const catalogBaseUrl = await discoveryApi.getBaseUrl(
        TESSA_LICENSE_TYPES_PLUGIN_ID,
      );
      const response = await fetchApi.fetch(
        `${catalogBaseUrl}${TESSA_LICENSE_TYPES_ROUTE_PATH}`,
        { signal: input.signal },
      );
      if (!response.ok) {
        throw new Error('Tessa license type picker request failed.');
      }
      return mapTessaLicenseTypesResponse(await response.json());
    },
  };
}

export function useTessaLicenseTypeSource() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useMemo(
    () => createTessaLicenseTypeSource({ discoveryApi, fetchApi }),
    [discoveryApi, fetchApi],
  );
}
